// Request layer for POST /metrics/playerAnalytics.
//
// Bitmovin is a single shared licence (~1,000 requests/hour across every
// customer, no server-side cache), so every call here is deliberate:
//   - identical requests within CACHE_TTL_MS are served from memory
//   - concurrent identical requests share one in-flight promise
//   - a monotonic request counter is exposed for budget reporting
//   - `createRequestGate` lets callers drop out-of-order responses

import MetricsService from '@/services/MetricsService';
import { perfRequestStart } from './analyticsPerf';

export const CACHE_TTL_MS = 60 * 1000;
// Core maps upstream Bitmovin errors (rate limit, timeout) to `[]`, the same
// shape as "no data", so an empty answer is only trusted briefly: long enough
// to dedupe a burst, short enough that a retry actually hits the server.
export const EMPTY_CACHE_TTL_MS = 5 * 1000;

const DEV = typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production';

const cache = new Map(); // key -> { expires, data }
const inflight = new Map(); // key -> Promise<{ data, fromCache }>
let requestCount = 0;
const countListeners = new Set();

export const METRIC_TYPES = {
	play: 'play',
	uniqueUsers: 'uniqueUsers',
	concurrentViewers: 'concurrentViewers',
	totalHoursWatched: 'totalHoursWatched',
	subtitles: 'subtitles',
	audio: 'audio',
};

function normalizeParams(params) {
	const selected = Array.isArray(params.selectedStreams) ? params.selectedStreams : [];
	return {
		startTime: new Date(params.startTime).toISOString(),
		endTime: new Date(params.endTime).toISOString(),
		type: params.type,
		interval: params.interval || undefined,
		groupBy: params.groupBy || undefined,
		filter: params.filter && params.filter.length ? params.filter : undefined,
		// Core silently caps at 10; keep the first 10 so the cache key matches
		// what the server actually evaluates.
		selectedStreams: selected.map(String).slice(0, 10),
	};
}

export function buildCacheKey(params) {
	const p = normalizeParams(params);
	return JSON.stringify([
		p.type,
		p.groupBy || null,
		p.filter || null,
		p.interval || null,
		p.startTime,
		p.endTime,
		[...p.selectedStreams].sort(),
	]);
}

/**
 * Fetch one analytics response. Resolves { data: Array, fromCache: boolean }.
 * Rejects only on transport errors; the backend maps upstream failures to []
 * so callers must treat an empty array as "no data or error".
 */
export function fetchAnalytics(params, { force = false } = {}) {
	const normalized = normalizeParams(params);
	if (!normalized.selectedStreams.length) {
		return Promise.resolve({ data: [], fromCache: true, skipped: true });
	}
	const key = buildCacheKey(normalized);
	const now = Date.now();
	if (!force) {
		const hit = cache.get(key);
		if (hit && hit.expires > now) return Promise.resolve({ data: hit.data, fromCache: true });
		if (inflight.has(key)) return inflight.get(key);
	}
	const promise = (async () => {
		requestCount += 1;
		countListeners.forEach((fn) => fn(requestCount));
		const done = perfRequestStart(`${normalized.type}${normalized.interval ? ` ${normalized.interval}` : ''}${normalized.groupBy ? ` by ${normalized.groupBy}` : ''}`);
		try {
			const raw = await MetricsService.getBitmovinAnalytics(normalized);
			const data = Array.isArray(raw) ? raw : [];
			cache.set(key, { expires: Date.now() + (data.length ? CACHE_TTL_MS : EMPTY_CACHE_TTL_MS), data });
			if (DEV && !data.length) {
				// eslint-disable-next-line no-console
				console.warn(
					`[analytics] empty response for ${normalized.type}${normalized.groupBy ? ` by ${normalized.groupBy}` : ''}${normalized.interval ? ` (${normalized.interval})` : ''}`
					+ ' — either no data or core swallowed a Bitmovin error (check core logs for a 429).',
					normalized,
				);
			}
			done();
			return { data, fromCache: false };
		} catch (e) {
			done({ failed: true });
			throw e;
		} finally {
			inflight.delete(key);
		}
	})();
	inflight.set(key, promise);
	return promise;
}

export function clearAnalyticsCache() {
	cache.clear();
}

export function getRequestCount() {
	return requestCount;
}

export function onRequestCount(fn) {
	countListeners.add(fn);
	return () => countListeners.delete(fn);
}

/**
 * Out-of-order protection: each `next()` invalidates previous tokens.
 */
export function createRequestGate() {
	let current = 0;
	return {
		next() {
			current += 1;
			return current;
		},
		isCurrent(token) {
			return token === current;
		},
		invalidate() {
			current += 1;
		},
	};
}

// ---------------------------------------------------------------- parsing

/**
 * Time series rows -> [[timestampMs, value]] sorted ascending.
 */
export function parseSeries(rows) {
	if (!Array.isArray(rows)) return [];
	const out = [];
	for (const row of rows) {
		if (!Array.isArray(row) || row.length < 2) continue;
		const ts = Number(row[0]);
		const value = Number(row[1]);
		if (!Number.isFinite(ts) || !Number.isFinite(value)) continue;
		out.push([ts, value]);
	}
	out.sort((a, b) => a[0] - b[0]);
	return out;
}

/**
 * groupBy rows -> [{ key, value }] sorted by value desc.
 */
export function parseGrouped(rows) {
	if (!Array.isArray(rows)) return [];
	const out = [];
	for (const row of rows) {
		if (!Array.isArray(row) || row.length < 2) continue;
		const value = Number(row[row.length - 1]);
		if (!Number.isFinite(value)) continue;
		const key = row[0] === null || row[0] === undefined ? null : String(row[0]);
		out.push({ key, value });
	}
	out.sort((a, b) => b.value - a.value);
	return out;
}

/**
 * No-interval, no-groupBy response -> single number or null when absent.
 * Core returns [[value]]; be tolerant of [value] and [[ts, value]].
 */
export function parseTotal(rows) {
	if (!Array.isArray(rows) || !rows.length) return null;
	const row = rows[0];
	if (Array.isArray(row)) {
		if (!row.length) return null;
		const v = Number(row[row.length - 1]);
		return Number.isFinite(v) ? v : null;
	}
	const v = Number(row);
	return Number.isFinite(v) ? v : null;
}

/**
 * Fill missing buckets with zero so charts show gaps as flat lines instead of
 * interpolating across them. Keeps at most `maxPoints`.
 */
export function fillSeries(series, from, to, stepMs, maxPoints = 400) {
	if (!stepMs || stepMs <= 0) return series;
	const start = Math.floor(new Date(from).getTime() / stepMs) * stepMs;
	const end = new Date(to).getTime();
	const byTs = new Map(series.map(([ts, v]) => [Math.floor(ts / stepMs) * stepMs, v]));
	const out = [];
	for (let ts = start, i = 0; ts <= end && i < maxPoints; ts += stepMs, i += 1) {
		out.push([ts, byTs.has(ts) ? byTs.get(ts) : 0]);
	}
	return out;
}
